import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import AdminLayout from '../../src/components/AdminLayout';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Search, Download, RefreshCw, Plus, Trash2, Edit2, X } from 'lucide-react';
import styles from '../../styles/Admin.module.css';

// ─── Custom Overlay Wrapper ───────────────────────────────────────────────────
function ModalOverlay({ children, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
    >
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative z-10 w-full">{children}</div>
    </div>
  );
}

export default function ManageCategories() {
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '', key_name: '', category_type: 'Category', title: '', h1: '', h2: '', thumb: '', icon: '', dis: '', page_dis: '', s_dis: ''
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  async function fetchCategories() {
    setLoading(true);
    const { data, error } = await supabase.from('categories').select('*').order('name');
    if (!error && data) {
      setCategories(data);
    }
    setLoading(false);
  }

  async function handleDelete(name) {
    if (confirm(`Are you sure you want to delete category "${name}"?`)) {
      await supabase.from('categories').delete().eq('name', name);
      fetchCategories();
    }
  }

  function openAddModal() {
    setIsEditing(false);
    setFormData({
      name: '', key_name: '', category_type: 'Category', title: '', h1: '', h2: '', thumb: '', icon: '', dis: '', page_dis: '', s_dis: ''
    });
    setIsModalOpen(true);
  }

  function openEditModal(cat) {
    setIsEditing(true);
    setFormData({
      name: cat.name || '',
      key_name: cat.key_name || '',
      category_type: cat.category_type || 'Category',
      title: cat.title || '',
      h1: cat.h1 || '',
      h2: cat.h2 || '',
      thumb: cat.thumb || '',
      icon: cat.icon || '',
      dis: cat.dis || '',
      page_dis: cat.page_dis || '',
      s_dis: cat.s_dis || ''
    });
    setIsModalOpen(true);
  }

  async function handleSave() {
    if (!formData.name) return alert('Name (Slug) is required!');
    
    if (isEditing) {
      await supabase.from('categories').update(formData).eq('name', formData.name);
    } else {
      await supabase.from('categories').insert(formData);
    }
    
    setIsModalOpen(false);
    fetchCategories();
  }

  const filteredCategories = categories.filter(cat => 
    (cat.key_name && cat.key_name.toLowerCase().includes(search.toLowerCase())) ||
    (cat.name && cat.name.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <AdminLayout>
      <div className="flex flex-col space-y-6 pb-20">
        
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
          <h1 className="text-3xl font-bold tracking-tight text-white">Manage Categories</h1>
          
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Search categories" 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-64 pl-9 bg-[#1e293b] border-slate-700 text-white placeholder:text-slate-500 rounded-full"
              />
            </div>

            <button onClick={fetchCategories} className="flex items-center bg-[#1e293b] hover:bg-[#334155] border border-slate-700 text-slate-300 px-4 py-2 rounded-full text-sm font-medium transition-colors">
              <RefreshCw className={`w-4 h-4 mr-2 text-indigo-400 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            
            <button onClick={openAddModal} className="flex items-center bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-full text-sm font-medium transition-colors">
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </button>
          </div>
        </div>

        {/* Categories Table */}
        <div className="rounded-2xl border border-slate-800 bg-[#172033] overflow-hidden">
          <Table>
            <TableHeader className="bg-[#1a2336] border-b border-slate-800">
              <TableRow className="hover:bg-transparent border-none">
                <TableHead className="w-16 text-slate-400 font-semibold text-xs tracking-wider uppercase pl-6">Thumb</TableHead>
                <TableHead className="text-slate-400 font-semibold text-xs tracking-wider uppercase">SLUG / TITLE</TableHead>
                <TableHead className="text-slate-400 font-semibold text-xs tracking-wider uppercase">KEY NAME</TableHead>
                <TableHead className="text-slate-400 font-semibold text-xs tracking-wider uppercase">TYPE</TableHead>
                <TableHead className="text-right text-slate-400 font-semibold text-xs tracking-wider uppercase pr-6">ACTIONS</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-slate-400">Loading...</TableCell>
                </TableRow>
              ) : filteredCategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-slate-400">No categories found.</TableCell>
                </TableRow>
              ) : (
                filteredCategories.map((cat, index) => (
                  <TableRow key={index} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                    <TableCell className="pl-6">
                      <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-xl overflow-hidden">
                        {cat.thumb ? <img src={cat.thumb} className="w-full h-full object-cover" /> : (cat.key_name?.charAt(0) || '🎮')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-semibold text-slate-200">{cat.name}</div>
                      <div className="text-xs text-slate-500 max-w-[200px] truncate">{cat.title || 'No title'}</div>
                    </TableCell>
                    
                    <TableCell>
                      <span className="text-purple-400 font-medium">{cat.key_name}</span>
                    </TableCell>
                    
                    <TableCell>
                      <Badge className={cat.category_type === 'Category' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}>
                        {cat.category_type}
                      </Badge>
                    </TableCell>

                    <TableCell className="text-right pr-6 space-x-3">
                      <button onClick={() => openEditModal(cat)} className="text-indigo-400 hover:text-indigo-300 text-sm font-medium">Edit</button>
                      <button onClick={() => handleDelete(cat.name)} className="text-rose-500 hover:text-rose-400 text-sm font-medium">Delete</button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <ModalOverlay onClose={() => setIsModalOpen(false)}>
          <div
            className="mx-auto bg-[#131f35] border border-slate-700/60 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            style={{ maxWidth: '900px', maxHeight: '94vh' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-8 py-5 border-b border-slate-800 bg-[#0f172a]/70 flex-shrink-0">
              <div className="flex items-center space-x-4">
                {formData.thumb && (
                  <img src={formData.thumb} alt="" className="w-12 h-12 rounded-lg object-cover border border-slate-700" onError={e => e.target.style.display='none'} />
                )}
                <div>
                  <h2 className="text-xl font-bold text-white">✏️ {isEditing ? 'Edit Category' : 'Add Category'}</h2>
                  <p className="text-xs text-slate-500 mt-0.5 font-mono">/{formData.name || 'new-category'}</p>
                </div>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-500 hover:text-white transition-colors p-2 rounded-lg hover:bg-slate-800">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable Body */}
            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="overflow-y-auto flex-1">
              <div className="px-8 py-6 space-y-5">
                
                <div>
                  <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">Slug (Primary Key) <span className="text-rose-400">*</span></label>
                  <input 
                    name="name" 
                    value={formData.name} 
                    onChange={e => setFormData({...formData, name: e.target.value})} 
                    disabled={isEditing}
                    required 
                    className="w-full bg-[#0a0f1e] border border-slate-700 text-white rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder:text-slate-600 transition-colors" 
                    placeholder="e.g. action-games" 
                  />
                </div>

                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">Key Name <span className="text-rose-400">*</span></label>
                    <input 
                      name="key_name" 
                      value={formData.key_name} 
                      onChange={e => setFormData({...formData, key_name: e.target.value})} 
                      className="w-full bg-[#0a0f1e] border border-slate-700 text-white rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder:text-slate-600 transition-colors" 
                      placeholder="e.g. Action games" 
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">Type</label>
                    <select 
                      name="category_type" 
                      value={formData.category_type} 
                      onChange={e => setFormData({...formData, category_type: e.target.value})}
                      className="w-full bg-[#0a0f1e] border border-slate-700 text-white rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer transition-colors"
                    >
                      <option value="Category">Category</option>
                      <option value="Tag">Tag</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">Title (SEO)</label>
                  <input 
                    name="title" 
                    value={formData.title} 
                    onChange={e => setFormData({...formData, title: e.target.value})} 
                    className="w-full bg-[#0a0f1e] border border-slate-700 text-white rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors" 
                  />
                </div>

                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">H1 Heading</label>
                    <input 
                      name="h1" 
                      value={formData.h1} 
                      onChange={e => setFormData({...formData, h1: e.target.value})} 
                      className="w-full bg-[#0a0f1e] border border-slate-700 text-white rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors" 
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">H2 Heading</label>
                    <input 
                      name="h2" 
                      value={formData.h2} 
                      onChange={e => setFormData({...formData, h2: e.target.value})} 
                      className="w-full bg-[#0a0f1e] border border-slate-700 text-white rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors" 
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">Thumbnail URL</label>
                  <input 
                    name="thumb" 
                    value={formData.thumb} 
                    onChange={e => setFormData({...formData, thumb: e.target.value})} 
                    className="w-full bg-[#0a0f1e] border border-slate-700 text-white rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors" 
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">Short Description (s_dis)</label>
                  <textarea 
                    name="s_dis" 
                    value={formData.s_dis} 
                    onChange={e => setFormData({...formData, s_dis: e.target.value})} 
                    rows="3" 
                    className="w-full bg-[#0a0f1e] border border-slate-700 text-white rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">HTML Description (dis)</label>
                  <textarea 
                    name="dis" 
                    value={formData.dis} 
                    onChange={e => setFormData({...formData, dis: e.target.value})} 
                    rows="5" 
                    className="w-full bg-[#0a0f1e] border border-slate-700 text-white rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono transition-colors"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end px-8 py-5 border-t border-slate-800 bg-[#0f172a]/70 flex-shrink-0 space-x-4">
                <button 
                  type="button" 
                  onClick={() => setIsModalOpen(false)} 
                  className="px-5 py-2.5 text-sm font-medium text-slate-300 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-6 py-2.5 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg text-sm font-bold shadow-lg shadow-indigo-500/20 transition-all active:scale-95"
                >
                  {isEditing ? 'Save Changes' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </ModalOverlay>
      )}

    </AdminLayout>
  );
}
